import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAuthor, updateAuthor } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from '../../hooks/use-toast';
import { ArrowLeft, Save } from 'lucide-react';

const AuthorEditor = () => {
  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    email: '',
    location: '',
    social: {
      twitter: '',
      instagram: ''
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAuthor();
  }, []);

  const fetchAuthor = async () => {
    try {
      const data = await getAuthor();
      setFormData(data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load author information",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSocialChange = (e) => {
    setFormData({
      ...formData,
      social: {
        ...formData.social,
        [e.target.name]: e.target.value
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await updateAuthor(formData);
      toast({
        title: "Author info updated",
        description: "Your profile has been updated successfully."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update author info. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Link
            to="/admin/dashboard"
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft size={16} />
            Back to Dashboard
          </Link>
          <h1 className="text-2xl font-serif text-gray-900">Edit Author Information</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 p-8 space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm uppercase tracking-wider text-gray-700 mb-2">
              Name *
            </label>
            <Input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm uppercase tracking-wider text-gray-700 mb-2">
              Email *
            </label>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div>
            <label htmlFor="location" className="block text-sm uppercase tracking-wider text-gray-700 mb-2">
              Location *
            </label>
            <Input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              required
            />
          </div>

          <div>
            <label htmlFor="bio" className="block text-sm uppercase tracking-wider text-gray-700 mb-2">
              Bio *
            </label>
            <Textarea
              id="bio"
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              required
              rows={12}
              placeholder="Write your author bio..."
            />
            <p className="text-xs text-gray-500 mt-2">
              Tip: Use double line breaks to separate paragraphs
            </p>
          </div>

          <div className="pt-6 border-t border-gray-200">
            <h3 className="text-lg font-serif text-gray-900 mb-4">Social Media</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="twitter" className="block text-sm uppercase tracking-wider text-gray-700 mb-2">
                  Twitter
                </label>
                <Input
                  type="text"
                  id="twitter"
                  name="twitter"
                  value={formData.social.twitter}
                  onChange={handleSocialChange}
                  placeholder="@username"
                />
              </div>
              <div>
                <label htmlFor="instagram" className="block text-sm uppercase tracking-wider text-gray-700 mb-2">
                  Instagram
                </label>
                <Input
                  type="text"
                  id="instagram"
                  name="instagram"
                  value={formData.social.instagram}
                  onChange={handleSocialChange}
                  placeholder="@username"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4 pt-6 border-t border-gray-200">
            <Button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-gray-900 text-white hover:bg-gray-800"
            >
              <Save size={16} />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Link to="/admin/dashboard">
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AuthorEditor;